// src/services/templateAuditService.js
//
// Append-only audit trail for a SourceX source-document template.
//
// All events are written to:
//   sourcex_template_audits/{templateId}/events/{eventId}
//
// Deliberately the same shape and the same guarantees as patientAuditService:
//
//   - Additive. It never reads or mutates template data, only writes events.
//   - Fail-soft. Audit logging must never break the parent save — every write
//     is wrapped and swallowed. A lost audit line is bad; a coordinator unable
//     to save their work because the audit collection was unreachable is worse.
//   - Its own collection, one document per event, never rewritten. An audit
//     trail that can be edited is not an audit trail.
//
// What it does NOT need that the patient trail does: masking. A template is a
// blank form shared by every patient in the study and holds no answers (see
// stripAnswers in templateSchema), so there is no PHI here to leak. The one
// rule that follows from that: never log a `value`. If a future change starts
// storing answers on a template, this file has to grow the same masking the
// patient trail has.

import { collection, addDoc, getDocs, limit, orderBy, query, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { getSourcexTemplateAuditEventsPath } from "./firestorePaths";
import { describeCondition } from "./templateSchema";

/** How many events the timeline reads at once. */
export const TEMPLATE_AUDIT_PAGE = 200;

// The actions a template can record. Kept as a closed set so the timeline can
// colour and label them without guessing at a free-text string.
export const TEMPLATE_AUDIT_ACTIONS = Object.freeze({
  QUESTION_ADDED: "QUESTION_ADDED",
  QUESTION_EDITED: "QUESTION_EDITED",
  QUESTION_DELETED: "QUESTION_DELETED",
  QUESTION_MOVED: "QUESTION_MOVED",
  CONDITION_CHANGED: "CONDITION_CHANGED",
  HEADING_ADDED: "HEADING_ADDED",
  HEADING_RENAMED: "HEADING_RENAMED",
  HEADING_DELETED: "HEADING_DELETED",
  SECTION_MERGED: "SECTION_MERGED",
  VISIT_ADDED: "VISIT_ADDED",
  VISIT_RENAMED: "VISIT_RENAMED",
  VISIT_DELETED: "VISIT_DELETED",
  VISIT_MOVED: "VISIT_MOVED",
  // A standing log added from "Add Log". Its own action, not VISIT_ADDED: a
  // log is not a visit, and a trail that called it one would read as though
  // someone had added a study visit named "Concomitant Medication Log".
  LOG_ADDED: "LOG_ADDED",
  // The INCLUSION/EXCLUSION CRITERIA checklist added from "Add
  // Inclusion/Exclusion". Its own action for the same reason LOG_ADDED is: it is
  // neither a visit nor a log, and the trail also has to record WHERE the
  // criteria came from — read from the study's protocol, or started blank —
  // because that is the first question anyone asks when a criterion is wrong.
  ELIGIBILITY_ADDED: "ELIGIBILITY_ADDED",
  TEMPLATE_APPROVED: "TEMPLATE_APPROVED",
  // A visit's source was marked as finalized on PAPER (or that mark removed).
  // Its own action rather than a question edit: it says nothing about what the
  // document contains, only that the authoritative copy is somewhere else —
  // and "who told us the real Screening source is on paper, and when?" is
  // exactly what gets asked when two copies turn up.
  PAPER_FINAL_MARKED: "PAPER_FINAL_MARKED",
  PAPER_FINAL_CLEARED: "PAPER_FINAL_CLEARED",
  VERSION_RESTORED: "VERSION_RESTORED",
  TEMPLATE_BUILT: "TEMPLATE_BUILT",
  // A coordinator hid (or brought back) the "RegX has a standard block for
  // this" notice on a section — see services/standardBlockAdvice.js. Recorded
  // because it is the record of a DECISION: this study is deliberately keeping
  // its own wording for a section the site standardizes, and "why doesn't this
  // one match?" is a question a monitor asks months later.
  STANDARD_BLOCK_NOTICE: "STANDARD_BLOCK_NOTICE",
  // A table was split back into individual questions. Its own action rather
  // than QUESTION_EDITED because it changes the SHAPE of the document — one
  // question becomes several, with new ids — and "why does this visit have
  // three questions where the last version had a log?" is exactly the kind of
  // thing a monitor asks about later.
  TABLE_SPLIT: "TABLE_SPLIT",
  // A whole section was copied into other visits. Worth its own action: it is
  // the record that several visits were changed by ONE deliberate act, which
  // reads very differently from somebody editing each of them by hand.
  BLOCK_COPIED: "BLOCK_COPIED",
  // One question was copied, into a heading of this visit or another visit
  // (2026-10-29). Its own action for the same reason: the new question is a
  // deliberate copy of an existing one, not something typed in fresh.
  QUESTION_COPIED: "QUESTION_COPIED",
});

/**
 * The question fields worth auditing, and what to call them in the timeline.
 * A closed list on purpose: it keeps internal bookkeeping (ids, provenance,
 * `value`) out of the trail, and gives every entry a human label without the
 * UI having to know the schema.
 */
export const AUDITED_QUESTION_FIELDS = Object.freeze([
  ["question", "Question text"],
  ["type", "Answer type"],
  ["required", "Required"],
  ["unit", "Unit"],
  ["units", "Unit choices"],
  ["options", "Choices"],
  ["defaultValue", "Starting value"],
  ["columns", "Table columns"],
  ["minRows", "Rows"],
  ["maxRows", "Maximum rows"],
  ["rowLabels", "Row labels"],
  ["rowLabelHeader", "Row-label heading"],
  ["prefillRows", "Table starting values"],
  ["visibleWhen", "Shown when"],
  ["instruction", "Instruction"],
  ["signatureRole", "Signature role"],
  ["attestation", "Attestation"],
]);

const FIELD_LABEL = new Map(AUDITED_QUESTION_FIELDS);

/** A field's human name, falling back to the raw key. */
export const auditFieldLabel = (field) => FIELD_LABEL.get(field) || field;

// ---------------------------------------------------------------------------
// Diffing
// ---------------------------------------------------------------------------

/**
 * JSON with object keys in a fixed order, at every depth.
 *
 * Plain JSON.stringify serialises keys in insertion order, which makes two
 * structurally identical values compare unequal purely because of how they
 * were built. That is not hypothetical here: buildCondition creates a rule as
 * `{questionId, op, value}`, and Firestore hands map keys back ALPHABETICALLY
 * — `{op, questionId, value}`. Comparing those two strings reported every
 * conditional question as changed on every comparison, and every table whose
 * starting values are keyed by column id along with it.
 *
 * Arrays keep their order: in a rule's `value`, or a table's columns, order is
 * meaning, not incidental.
 */
const stableJson = (v) => {
  if (v === null || typeof v !== "object") return JSON.stringify(v) ?? "null";
  if (Array.isArray(v)) return `[${v.map(stableJson).join(",")}]`;
  return `{${Object.keys(v)
    .sort()
    .map((k) => `${JSON.stringify(k)}:${stableJson(v[k])}`)
    .join(",")}}`;
};

// Same intent as patientAuditService's valuesEqual — decide whether a field
// really changed — but without its string/number coercion. Template data is
// typed all the way through (it is built by makeQuestion, never by a
// stringly-typed sheet), so treating "5" and 5 as equal here would hide a
// genuine type change rather than paper over a storage quirk.
const valuesEqual = (a, b) => {
  if (a === b) return true;
  if (a == null && b == null) return true;
  if (a == null || b == null) return false;
  if (typeof a !== typeof b) return false;
  if (typeof a === "object") {
    try {
      return stableJson(a) === stableJson(b);
    } catch {
      return false;
    }
  }
  return false;
};

/**
 * Render one field value as a short string for the timeline.
 *
 * Structural values (a table's columns, a condition) are summarised rather
 * than dumped: "3 columns" tells a reviewer what changed and points them at
 * the version redline for the detail, whereas a wall of JSON in an audit row
 * gets skipped by everyone.
 */
export const describeAuditValue = (field, value, labelById) => {
  if (value === undefined || value === null || value === "") return "—";
  if (typeof value === "boolean") return value ? "yes" : "no";
  if (field === "columns") {
    const n = Array.isArray(value) ? value.length : 0;
    return `${n} column${n === 1 ? "" : "s"}`;
  }
  if (field === "prefillRows") {
    const n = Array.isArray(value) ? value.length : 0;
    return `${n} row${n === 1 ? "" : "s"} of starting values`;
  }
  if (field === "visibleWhen") {
    // The actual rule, not the word "a condition" — a row reading
    // "Shown when: a condition -> a condition" tells a reviewer nothing and
    // looks like a bug even when the rule genuinely did change.
    const d = describeCondition(value, labelById || new Map());
    if (d.complex) return "an advanced rule";
    if (!d.parts.length) return "always";
    return d.parts.join(d.mode === "all" ? " and " : " or ");
  }
  if (Array.isArray(value)) {
    if (!value.length) return "—";
    const flat = value.every((v) => typeof v !== "object");
    return flat ? value.join(", ") : `${value.length} item${value.length === 1 ? "" : "s"}`;
  }
  if (typeof value === "object") return "changed";
  return String(value).slice(0, 200);
};

/**
 * A structured diff between two versions of one question.
 *
 * @param {object} before  the question as it was (or {} for a new one)
 * @param {object} after   the question as it now is
 * @returns {Array<{field: string, label: string, from: string, to: string}>}
 */
export const buildQuestionDiff = (before = {}, after = {}, labelById) => {
  const changes = [];
  AUDITED_QUESTION_FIELDS.forEach(([field, label]) => {
    const a = before ? before[field] : undefined;
    const b = after ? after[field] : undefined;
    if (a === undefined && b === undefined) return;
    if (valuesEqual(a, b)) return;
    changes.push({
      field,
      label,
      from: describeAuditValue(field, a, labelById),
      to: describeAuditValue(field, b, labelById),
    });
  });
  return changes;
};

// ---------------------------------------------------------------------------
// Writing
// ---------------------------------------------------------------------------

/**
 * Append one audit event. Never throws.
 *
 * @param {string} args.templateId  the sourcex_templates doc id
 * @param {string} args.action      one of TEMPLATE_AUDIT_ACTIONS
 * @param {string} args.title       one line, already human-readable
 * @param {string} [args.eventId]   the visit this happened in — the timeline is
 *   read per visit, so an event without one only shows in the "whole template"
 *   view (approvals, rebuilds, visit add/delete)
 * @param {string} [args.eventName] that visit's name at the time
 * @param {string} [args.questionId]
 * @param {string} [args.questionLabel]  e.g. "1.2 Weight" — the number makes an
 *   entry findable in the document months later
 * @param {Array}  [args.changes]   from buildQuestionDiff
 * @param {object} [args.actor]     { email, name, initials }
 * @param {object} [args.metadata]
 */
export const logTemplateAuditEvent = async ({
  templateId,
  action,
  title,
  eventId = "",
  eventName = "",
  questionId = "",
  questionLabel = "",
  changes = [],
  actor = {},
  metadata = {},
} = {}) => {
  if (!templateId || !action || !title) return;
  try {
    await addDoc(collection(db, getSourcexTemplateAuditEventsPath(templateId)), {
      templateId,
      action,
      title,
      eventId,
      eventName,
      questionId,
      questionLabel,
      changes,
      actorEmail: actor.email || "unknown",
      actorName: actor.name || "",
      actorInitials: actor.initials || "",
      actorUid: actor.uid || null,
      metadata,
      createdAt: serverTimestamp(),
    });
  } catch (err) {
    // Audit must never break the parent flow.
    // eslint-disable-next-line no-console
    console.error("❌ logTemplateAuditEvent failed:", err);
  }
};

/**
 * The trail for one template, newest first. Returns [] on failure rather than
 * throwing — a timeline that can't load must not take the editor down with it.
 * @param {string} templateId
 * @returns {Promise<Array>}
 */
export const listTemplateAuditEvents = async (templateId) => {
  if (!templateId) return [];
  try {
    const snap = await getDocs(
      query(
        collection(db, getSourcexTemplateAuditEventsPath(templateId)),
        orderBy("createdAt", "desc"),
        limit(TEMPLATE_AUDIT_PAGE)
      )
    );
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error("❌ listTemplateAuditEvents failed:", err);
    return [];
  }
};
